import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useCanManageChecklistItems } from "./useChecklistItemPermissions";
import {
  useGetChecklistItemsQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
} from "./checklistItemsApi";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// Branch-wise master feeding the Technical module's Service Report checklist
// (battery volt, PIR tested, etc., 2026-09-02) - lets the business define its
// own item list instead of a hardcoded one, mirroring Sales' Service Types
// master pattern. A single Technical.ManageChecklistItems permission gates
// this whole screen (no separate view permission - see
// feedback_no_separate_view_permission_for_masters). No Delete: once an item
// has been used on a real report, removing it would orphan that value, so
// retiring an item is the Active checkbox only.
const ChecklistItemsPage = () => {
  const canManage = useCanManageChecklistItems();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [status, setStatus] = useState("active");
  const { data: records = [], isLoading } = useGetChecklistItemsQuery(
    {
      branchId: selectedBranchId ?? undefined,
      isActive: status === "" ? undefined : status === "active",
    },
    { skip: !canManage }
  );
  const [createChecklistItem, { isLoading: isCreating }] = useCreateChecklistItemMutation();
  const [updateChecklistItem, { isLoading: isUpdating }] = useUpdateChecklistItemMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [itemName, setItemName] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [search, setSearch] = useState("");

  const selectedRecord = records.find((r) => r.checklistItemId === selectedId) || null;

  const filteredRecords = search.trim()
    ? records.filter((r) => r.itemName.toLowerCase().includes(search.trim().toLowerCase()))
    : records;

  useEffect(() => {
    if (mode === "new") {
      setItemName("");
      setDisplayOrder(0);
      setIsActive(true);
      setBranchId(
        selectedBranchId ? String(selectedBranchId) : availableBranches[0]?.branchId ? String(availableBranches[0].branchId) : ""
      );
    } else if (selectedRecord) {
      setBranchId(String(selectedRecord.branchId));
      setItemName(selectedRecord.itemName);
      setDisplayOrder(selectedRecord.displayOrder);
      setIsActive(selectedRecord.isActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = canManage;

  const select = (id) => {
    setSelectedId(id);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedId(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!itemName.trim()) {
      toast.error("Item name is required");
      return;
    }
    try {
      if (mode === "edit" && selectedRecord) {
        await updateChecklistItem({
          checklistItemId: selectedRecord.checklistItemId,
          itemName: itemName.trim(),
          displayOrder: Number(displayOrder) || 0,
          isActive,
        }).unwrap();
      } else {
        await createChecklistItem({
          branchId: Number(branchId),
          itemName: itemName.trim(),
          displayOrder: Number(displayOrder) || 0,
        }).unwrap();
      }
      toast.success("Checklist item saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save checklist item");
    }
  };

  const columns = [
    { title: "Item Name", dataIndex: "itemName" },
    { title: "Order", dataIndex: "displayOrder", width: 80 },
    {
      title: "Branch",
      dataIndex: "branchId",
      render: (value) => availableBranches.find((b) => b.branchId === value)?.branchCode || value,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <span className={`badge ${value ? "badge-linesuccess" : "badge-linedanger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.checklistItemId)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  if (!canManage) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-info">You do not have permission to view Service Report checklist items.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Service Report Checklist Items</h4>
              <h6>Per-branch checklist a technician fills on a Service Report (battery volt, PIR tested, etc.)</h6>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select
                  className="form-control"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={isReadOnly || mode === "edit" || !canWrite}
                >
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Item Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Display Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              {mode !== "new" && (
                <div className="col-md-2 mb-3">
                  <label className="form-label d-block">Active</label>
                  <div className="form-check form-switch mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={isReadOnly || !canWrite}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex gap-2">
              {mode !== "view" && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isCreating || isUpdating}
                >
                  Save
                </button>
              )}
              {mode === "view" && (
                <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                  Edit
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={startNew}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search item name"
                    className="form-control form-control-sm formsearch"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredRecords}
                rowKey="checklistItemId"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistItemsPage;
